import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Loader2, ChevronRight, CalendarClock, Layers, Puzzle, PenLine, CheckCircle2 } from "lucide-react";

const SIG = {
  signed: { text: "Signed", cls: "bg-emerald-50 text-emerald-600", Icon: CheckCircle2 },
  awaiting: { text: "Awaiting signature", cls: "bg-amber-50 text-amber-600", Icon: PenLine },
  none: null,
};

const LEVELS = [
  { key: "classroom", label: "Classroom Support", sub: "All / most pupils", tint: "#81B29A" },
  { key: "school", label: "School Support", sub: "Some pupils", tint: "#6C8EBF" },
  { key: "school_plus", label: "School Support Plus", sub: "A few pupils", tint: "#C0698D" },
  { key: "none", label: "No plan yet", sub: "Not on the continuum", tint: "#94a3b8" },
];
const FLAG = {
  overdue: { text: "Review overdue", cls: "bg-red-50 text-red-600" },
  due_soon: { text: "Review due soon", cls: "bg-amber-50 text-amber-600" },
  scheduled: { text: "Review scheduled", cls: "bg-emerald-50 text-emerald-600" },
  none: null,
};

export default function Continuum() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get(`/classes/${classId}/cos-overview`).then((r) => setRows(r.data.students)).catch(() => navigate("/"));
  }, [classId, navigate]);

  if (!rows) return <div className="min-h-screen flex justify-center items-center text-slate-300"><Loader2 className="animate-spin" /></div>;

  const byLevel = (k) => rows.filter((s) => (s.level || "none") === k);

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16">
      <div className="max-w-5xl mx-auto px-5 py-6">
        <button data-testid="continuum-back-btn" onClick={() => navigate(`/class/${classId}`)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-5">
          <ArrowLeft size={16} /> Back to class
        </button>
        <div className="flex items-center gap-2.5 mb-1">
          <span className="h-9 w-9 rounded-xl bg-[#7FB069] flex items-center justify-center text-white"><Layers size={18} /></span>
          <h1 className="font-['Outfit'] text-3xl font-bold text-slate-800">Continuum of Support</h1>
        </div>
        <p className="text-slate-500 mb-7">Every pupil grouped by their current level of support, with review reminders.</p>

        <div className="grid md:grid-cols-3 gap-4">
          {LEVELS.filter((l) => l.key !== "none").map((l) => {
            const list = byLevel(l.key);
            return (
              <div key={l.key} data-testid={`continuum-col-${l.key}`} className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100" style={{ borderTop: `3px solid ${l.tint}` }}>
                  <p className="font-semibold font-['Outfit'] text-slate-800 flex items-center justify-between">
                    {l.label}
                    <span className="text-sm rounded-full px-2.5 py-0.5" style={{ backgroundColor: `${l.tint}18`, color: l.tint }}>{list.length}</span>
                  </p>
                  <p className="text-xs text-slate-400">{l.sub}</p>
                </div>
                <div className="p-3 space-y-2 min-h-[80px]">
                  {list.length === 0 && <p className="text-xs text-slate-300 text-center py-4">No pupils</p>}
                  {list.map((s) => <StudentRow key={s.id} s={s} onClick={() => navigate(`/student/${s.id}/cos`)} />)}
                </div>
              </div>
            );
          })}
        </div>

        {byLevel("none").length > 0 && (
          <div className="mt-6 bg-white rounded-2xl border border-slate-100 p-4">
            <p className="font-semibold font-['Outfit'] text-slate-700 mb-3">Not yet on the continuum <span className="text-slate-400 font-normal">({byLevel("none").length})</span></p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {byLevel("none").map((s) => <StudentRow key={s.id} s={s} onClick={() => navigate(`/student/${s.id}/cos`)} muted />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StudentRow({ s, onClick, muted }) {
  const flag = FLAG[s.review_flag];
  const sig = SIG[s.signature];
  return (
    <button data-testid={`continuum-student-${s.id}`} onClick={onClick}
      className="w-full text-left rounded-xl border border-slate-100 p-3 hover:border-[#7FB069] hover:shadow-sm transition group">
      <div className="flex items-center justify-between">
        <span className="font-semibold font-['Outfit'] text-slate-800 flex items-center gap-1.5 truncate">
          {s.name}
          {s.is_asd && <Puzzle size={13} className="text-[#3E9B96] shrink-0" />}
        </span>
        <ChevronRight size={16} className="text-slate-300 group-hover:text-[#7FB069] shrink-0" />
      </div>
      {!muted && (
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <span className="text-xs text-slate-400">{s.target_count} target{s.target_count === 1 ? "" : "s"} · {s.review_count} review{s.review_count === 1 ? "" : "s"}</span>
          {flag && <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${flag.cls}`}><CalendarClock size={11} /> {flag.text}</span>}
          {sig && <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${sig.cls}`}><sig.Icon size={11} /> {sig.text}</span>}
        </div>
      )}
      {muted && <p className="text-xs text-slate-400 mt-1">Tap to start a plan</p>}
    </button>
  );
}
